'use client';
import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import WaSmartButton from './WaSmartButton';
import ContactButton from './ContactButton';
import { useSmartIntakeCtx } from '@/lib/smartIntakeStore';
import { fmtRating } from '@/lib/format';
import { isWhatsAppCapable } from '@/lib/phoneValidation';
import { avatarImg } from '@/lib/imgUrl';

export type OtherExpert = {
  id: number;
  href: string;
  firstName: string;
  fullName: string;
  phone?: string;
  whatsappValid?: number | null;
  gender?: string | null;
  imgSrc: string;
  rating10: number;
  reviewCount: number;
  categorySlug: string;
  categoryName: string;
};

// "Get more quotes easily" — same-category experts, each with name, photo, rating
// and a WhatsApp button that sends the same (context-aware) request, name swapped.
export default function OtherExperts({ experts }: { experts: OtherExpert[] }) {
  const ctx = useSmartIntakeCtx();
  const filled = !!(ctx?.cleanNeed || ctx?.customNeed);
  const [highlight, setHighlight] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // A message was sent / a call was placed somewhere on this page — if THIS
  // instance is the currently-visible one (mobile vs. desktop sidebar each
  // render their own copy, one always hidden via CSS), scroll to it and pulse
  // a highlight so users naturally flow into "get more quotes" next.
  useEffect(() => {
    function onContacted() {
      const el = ref.current;
      if (!el || el.offsetParent === null) return; // hidden instance — skip
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlight(true);
      setTimeout(() => setHighlight(false), 2400);
    }
    window.addEventListener('experts:contacted', onContacted);
    return () => window.removeEventListener('experts:contacted', onContacted);
  }, []);

  if (!experts.length) return null;
  return (
    <div ref={ref} className={highlight ? 'oe-glow' : ''} style={{ borderRadius: 16 }}>
    <div className="rounded-2xl overflow-hidden" style={{ background: '#fff', border: '1px solid #e2e8f0' }}>
      {/* Blue header (site style), white text */}
      <div className="px-4 py-3" style={{ background: 'linear-gradient(135deg,#0d47a1,#1976d2)' }}>
        <h3 className="text-sm font-black flex items-center gap-1.5 text-white">
          💬 קבל הצעות נוספות בקלות
        </h3>
        <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,.85)' }}>שלח את הבקשה למומחים נוספים</p>
      </div>

      {/* Prompt — shown until the questionnaire is filled */}
      {!filled && (
        <div className="px-4 py-2.5 flex items-center gap-2" style={{ background: '#f0f9ff', borderBottom: '1px solid #e0f2fe' }}>
          <span style={{ fontSize: 15 }}>✨</span>
          <span className="text-xs font-semibold" style={{ color: '#075985', lineHeight: 1.45 }}>
            השלם את השאלון הקצר לניסוח הודעה — ואז תוכל לשלוח אותה גם למומחים כאן
          </span>
        </div>
      )}

      <div className="divide-y" style={{ borderColor: '#f1f5f9' }}>
        {experts.map(e => (
          <div key={e.id} className="p-3.5">
            <div className="flex items-center gap-3 mb-2.5">
              <Link href={e.href} className="flex-shrink-0">
                <img {...avatarImg(e.imgSrc, 48, { kind: 'pro', ctx: { category: e.categoryName, proName: e.firstName } })} width={48} height={48} loading="lazy" decoding="async"
                  className="rounded-full object-cover"
                  style={{ width: 48, height: 48, border: '2px solid #e0e7ff' }} />
              </Link>
              <Link href={e.href} className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-base font-black truncate" style={{ color: '#0f172a' }}>{e.firstName}</span>
                  <span style={{ color: '#f59e0b', fontSize: '1rem', lineHeight: 1 }}>★</span>
                  <span className="font-black text-sm" style={{ color: '#0d47a1' }}>{fmtRating(e.rating10)}</span>
                  <span className="text-xs" style={{ color: '#94a3b8' }}>({e.reviewCount})</span>
                </div>
                <span className="text-xs" style={{ color: '#64748b' }}>{e.categoryName}</span>
              </Link>
            </div>
            {e.phone && (
              isWhatsAppCapable(e.phone, e.whatsappValid) ? (
                <WaSmartButton proArea={null}
                  proName={e.fullName} proFirstName={e.firstName} proPhone={e.phone} whatsappValid={e.whatsappValid}
                  proId={e.id} proGender={e.gender} categorySlug={e.categorySlug} categoryName={e.categoryName}
                  slim buttonLabel={`שליחת ההודעה שלי ל${e.firstName}`}
                />
              ) : (
                // 2026-07-30: WaSmartButton silently renders nothing for a non-WhatsApp
                // -capable phone (landline/special-service number) — this card has no
                // other button, so without this fallback the expert would show with no
                // way to contact them at all. A landline can still take a call.
                <ContactButton
                  proName={e.fullName} proFirstName={e.firstName} proPhone={e.phone}
                  proId={e.id} proGender={e.gender} categorySlug={e.categorySlug} categoryName={e.categoryName}
                  label={`התקשר ל${e.firstName}`}
                  className="flex items-center justify-center gap-2 w-full py-2.5 px-3 rounded-xl text-sm font-bold text-white"
                />
              )
            )}
          </div>
        ))}
      </div>
    </div>
    <style>{`
      @keyframes oeGlow {
        0%   { box-shadow: 0 0 0 rgba(13,71,161,0); }
        50%  { box-shadow: 0 0 0 6px rgba(13,71,161,.18); }
        100% { box-shadow: 0 0 0 rgba(13,71,161,0); }
      }
      .oe-glow { animation: oeGlow 1.2s ease-in-out 2; }
    `}</style>
    </div>
  );
}
